/**
 * Autocapture content stage of the PostHog `before_send` hook.
 *
 * `mask_all_text` and `mask_all_element_attributes` stop the SDK recording most
 * element content, but not all of it: posthog-js reads the nearest anchor's
 * `href` independently of attribute masking, writes it onto the clicked
 * element (so it is serialised into `$elements_chain` as `href="…"`), and
 * copies an off-host one to `$external_click_url`. A list row that links to a
 * person's page therefore still sends that person's id on every click.
 *
 * This stage keeps only what an element is and where it sits — tag, classes,
 * sibling position — and drops everything else. It is an allowlist, so a key a
 * future SDK version adds is dropped by default, and a payload it cannot read
 * drops the event rather than sending it.
 */

const AUTOCAPTURE_FAMILY = new Set(['$autocapture', '$rageclick', '$dead_click']);

/** Event-level properties that carry element content. */
const CONTENT_PROPERTIES = ['$el_text', '$external_click_url'] as const;

/** Attributes of a serialised chain element that survive. Both are positions. */
const KEPT_CHAIN_ATTRIBUTES = new Set(['nth-child', 'nth-of-type']);

/** Keys of an `$elements` entry that survive. */
const KEPT_ELEMENT_KEYS = ['tag_name', 'classes', 'nth_child', 'nth_of_type'] as const;

/** A chain attribute key followed by its opening quote, anchored at `lastIndex`. */
const ATTRIBUTE_START = /[A-Za-z0-9_-]+="/y;

const POSITION = /^\d+$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function attributeStartsAt(chain: string, index: number): boolean {
  ATTRIBUTE_START.lastIndex = index;
  return ATTRIBUTE_START.test(chain);
}

/**
 * Rewrite a serialised element chain (`tag.cls:k="v"k2="v2";tag…`) keeping each
 * element's `tag.classes` head and its position attributes. Returns null when
 * the chain cannot be read.
 *
 * Values are quote-escaped (`\"`) and may contain `;` and `:`, so the chain is
 * scanned rather than split. The head may contain `:` too (`hover:bg-…` class
 * names) but never a quote, so an element's attributes begin at the first `:`
 * followed by `key="`. A kept value must be a plain number: if a value with an
 * unusual escape knocked the scan out of step, a position attribute would no
 * longer read as one, and the event is dropped rather than misread.
 */
export function maskElementsChain(chain: string): string | null {
  const elements: string[] = [];
  let index = 0;
  while (index < chain.length) {
    const headStart = index;
    while (index < chain.length && !(chain[index] === ':' && attributeStartsAt(chain, index + 1))) {
      if (chain[index] === '"' || chain[index] === ';') return null;
      index++;
    }
    if (index >= chain.length) return null;
    const head = chain.slice(headStart, index);
    index++;

    const kept: string[] = [];
    while (attributeStartsAt(chain, index)) {
      const keyEnd = chain.indexOf('=', index);
      const key = chain.slice(index, keyEnd);
      index = keyEnd + 2;
      const valueStart = index;
      while (index < chain.length && chain[index] !== '"') {
        index += chain[index] === '\\' ? 2 : 1;
      }
      if (index >= chain.length) return null;
      const value = chain.slice(valueStart, index);
      index++;
      if (KEPT_CHAIN_ATTRIBUTES.has(key)) {
        if (!POSITION.test(value)) return null;
        kept.push(`${key}="${value}"`);
      }
    }
    elements.push(`${head}:${kept.join('')}`);

    if (index === chain.length) break;
    if (chain[index] !== ';') return null;
    index++;
    if (index === chain.length) return null;
  }
  return elements.join(';');
}

function maskElement(element: unknown): Record<string, unknown> | null {
  if (!isRecord(element)) return null;
  const out: Record<string, unknown> = {};
  for (const key of KEPT_ELEMENT_KEYS) {
    if (key in element) out[key] = element[key];
  }
  return out;
}

/**
 * Strip element content from an autocapture-family event, in place. Returns
 * false when the event must be dropped because its element data cannot be read.
 * Every other event is left alone.
 */
export function maskAutocaptureContent(event: {
  event: string;
  properties: Record<string, unknown>;
}): boolean {
  if (!AUTOCAPTURE_FAMILY.has(event.event)) return true;
  const { properties } = event;
  for (const key of CONTENT_PROPERTIES) delete properties[key];

  const chain = properties.$elements_chain;
  if (chain !== undefined) {
    if (typeof chain !== 'string') return false;
    const masked = maskElementsChain(chain);
    if (masked === null) return false;
    properties.$elements_chain = masked;
  }

  const elements = properties.$elements;
  if (elements !== undefined) {
    if (!Array.isArray(elements)) return false;
    const masked: Record<string, unknown>[] = [];
    for (const element of elements) {
      const kept = maskElement(element);
      if (kept === null) return false;
      masked.push(kept);
    }
    properties.$elements = masked;
  }
  return true;
}
